import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import AceEditor from "../../../../utils/aceEditor";
import "ace-builds/src-noconflict/mode-python";
import "ace-builds/src-noconflict/theme-dreamweaver";
import type { CodingPreviewModel } from "./questionPreviewAdapter";

type Props = { model: CodingPreviewModel };

type TestCaseResult = {
  id: string;
  passed: boolean;
  detail?: string;
  input?: string;
  expected?: string;
  actual?: string;
};

type PyExeStatusResponse = {
  status?: string;
  result?: {
    success?: boolean;
    actual_output?: string;
    error?: string | null;
    parsed_results?: Array<{
      passed?: boolean;
      input?: string;
      expected?: string;
      actual?: string;
      result?: unknown;
    }>;
  };
  error?: string | null;
};

const pyexeBase = () =>
  (process.env.REACT_APP_PYEXE_BASE_URL || "").replace(/\/?$/, "/");

const extractMandatoryKeywords = (testCases: unknown[]): string[] => {
  if (!testCases?.length) return [];
  const first = testCases[0] as { Testcase?: unknown };
  if (!first?.Testcase) return [];
  const value = Array.isArray(first.Testcase)
    ? first.Testcase
    : (first.Testcase as { Value?: string[] })?.Value;
  if (!Array.isArray(value)) return [];
  return value.filter(
    (keyword) => typeof keyword === "string" && !keyword.includes("def")
  );
};

/** Pixel-faithful PythonEditorComponent layout (testing context: RUN CODE only). */
const PythonCodingPreview: React.FC<Props> = ({ model }) => {
  const [code, setCode] = useState(model.template);
  const [running, setRunning] = useState(false);
  const [output, setOutput] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [additionalMessage, setAdditionalMessage] = useState("");
  const [results, setResults] = useState<TestCaseResult[]>([]);
  const [activeSection, setActiveSection] = useState<"output" | "testcases">(
    "output"
  );
  const [selectedTestCaseIndex, setSelectedTestCaseIndex] = useState<
    number | null
  >(null);

  useEffect(() => {
    setCode(model.template);
    setOutput("");
    setSuccessMessage("");
    setAdditionalMessage("");
    setResults([]);
    setActiveSection("output");
    setSelectedTestCaseIndex(null);
  }, [model]);

  const mandatoryKeywords = useMemo(
    () => extractMandatoryKeywords(model.testCases),
    [model.testCases]
  );

  const runPython = async () => {
    const base = pyexeBase();
    if (!base) {
      const cases = Array.isArray(model.testCases) ? model.testCases : [];
      const scored: TestCaseResult[] = [];
      let idx = 0;
      for (const raw of cases) {
        const row = raw as { Testcase?: unknown };
        if (!row?.Testcase || Array.isArray(row.Testcase)) continue;
        const tc = row.Testcase as { Value?: string[]; Output?: string };
        if (!tc?.Output) continue;
        idx += 1;
        const needle = String(tc.Output).trim();
        const passed =
          code.includes(needle) ||
          (model.answer &&
            code
              .replace(/\s+/g, " ")
              .includes(model.answer.replace(/\s+/g, " ").slice(0, 40)));
        scored.push({
          id: `TestCase${idx}`,
          passed: Boolean(passed),
          input: Array.isArray(tc.Value) ? tc.Value.join("\n") : "",
          expected: tc.Output,
          actual: passed ? tc.Output : "",
          detail: tc.Output,
        });
      }
      if (scored.length === 0) {
        scored.push({
          id: "Result",
          passed: code.trim().length > 20 && !code.includes("pass\n"),
        });
      } else {
        scored.push({
          id: "Result",
          passed: scored.every((r) => r.passed),
        });
      }
      setResults(scored);
      setOutput(
        model.examples[0]?.output
          ? String(model.examples[0].output)
          : "Run finished (local check — set REACT_APP_PYEXE_BASE_URL for live execution)."
      );
      const allPassed = scored
        .filter((r) => r.id !== "Result")
        .every((r) => r.passed);
      setSuccessMessage(allPassed ? "Congratulations!" : "Wrong Answer");
      setAdditionalMessage(
        allPassed
          ? "You have passed all the test cases."
          : "You have not passed all the test cases."
      );
      setActiveSection("output");
      setSelectedTestCaseIndex(0);
      return;
    }

    const transformed = (model.testCases || []).map((testCase, index) => {
      const row = testCase as { Testcase?: unknown };
      if (index === 0 && Array.isArray(row?.Testcase)) {
        return { Testcase: row.Testcase };
      }
      if (row?.Testcase && typeof row.Testcase === "object" && !Array.isArray(row.Testcase)) {
        const tc = row.Testcase as { Value?: string[]; Output?: string };
        if (tc?.Value) {
          return { Testcase: { Value: tc.Value, Output: tc.Output } };
        }
      }
      if (Array.isArray(row?.Testcase)) {
        return {
          Testcase: { Value: row.Testcase, Output: "validation_check" },
        };
      }
      return { Testcase: { Value: [], Output: "validation_check" } };
    });

    const submit = await axios.post(`${base}api/v1/submit`, {
      code,
      TestCases: transformed,
      FunctionCall: model.functionCall || "",
      language: "python",
      timeout: 10,
      memory_limit: "100m",
      user_id: "admin-preview",
      question_id: model.question_id,
      test_id: "preview",
    });
    const submissionId = submit.data?.submission_id;
    if (!submissionId) throw new Error("No submission_id from PyExe");

    const started = Date.now();
    while (Date.now() - started < 30000) {
      const status = await axios.post(
        `${base}api/v1/execute/${submissionId}`,
        {}
      );
      const data = status.data as PyExeStatusResponse;
      if (data?.status === "completed") {
        const result = data.result;
        const actualOutput =
          result?.actual_output != null
            ? String(result.actual_output)
            : data.error
              ? `Error: ${data.error}`
              : "";

        if (result?.success) {
          setOutput(actualOutput || "(no stdout)");

          if (Array.isArray(result.parsed_results)) {
            const testCaseResults: TestCaseResult[] =
              result.parsed_results.map((testCase, index) => ({
                id: `TestCase${index + 1}`,
                passed: Boolean(testCase.passed),
                input:
                  testCase.input != null ? String(testCase.input) : undefined,
                expected:
                  testCase.expected != null
                    ? String(testCase.expected)
                    : undefined,
                actual:
                  testCase.actual != null
                    ? String(testCase.actual)
                    : testCase.result != null
                      ? String(testCase.result)
                      : undefined,
                detail:
                  testCase.actual != null
                    ? String(testCase.actual)
                    : undefined,
              }));
            const allPassed = result.parsed_results.every((t) => t.passed);
            testCaseResults.push({
              id: "Result",
              passed: allPassed,
              detail: allPassed ? "Passed" : "Failed",
            });
            setResults(testCaseResults);
            if (allPassed) {
              setSuccessMessage("Congratulations!");
              setAdditionalMessage(
                "You have passed all the test cases. Click the submit code button."
              );
            } else {
              setSuccessMessage("Wrong Answer");
              setAdditionalMessage(
                "You have not passed all the test cases."
              );
            }
          } else {
            setResults([{ id: "Result", passed: true, detail: "Passed" }]);
            setSuccessMessage("Congratulations!");
            setAdditionalMessage(
              "You have passed all the test cases."
            );
          }
        } else {
          const errorMessage = result?.error || data.error || "Execution failed";
          setOutput(`Error: ${errorMessage}`);
          setSuccessMessage("Execution failed");
          setAdditionalMessage("Code has compilation or runtime errors");
          const errorCases: TestCaseResult[] = (model.testCases || []).map(
            (_, index) => ({
              id: `TestCase${index + 1}`,
              passed: false,
              detail: "Error occurred during execution",
            })
          );
          errorCases.push({
            id: "Result",
            passed: false,
            detail: "Failed due to execution error",
          });
          setResults(errorCases);
        }

        setActiveSection("output");
        setSelectedTestCaseIndex(0);
        return;
      }
      await new Promise((r) => setTimeout(r, 1000));
    }
    throw new Error("PyExe execution timed out");
  };

  const handleRunCode = async () => {
    if (!code.trim()) return;
    setRunning(true);
    setSuccessMessage("");
    setAdditionalMessage("");
    setOutput("");
    setResults([]);
    try {
      await runPython();
    } catch (err: unknown) {
      console.error(err);
      setSuccessMessage("Execution failed");
      setAdditionalMessage(
        (err as { message?: string })?.message ||
          "Network or API error occurred"
      );
      setOutput(
        (err as { message?: string })?.message ||
          "Unable to execute. Check PyExe configuration."
      );
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      className="d-flex"
      style={{ height: "70vh", width: "100%", gap: "0" }}
    >
      <div
        className="bg-white"
        style={{
          width: "40%",
          flexShrink: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          marginRight: "10px",
        }}
      >
        <div
          className="bg-white"
          style={{
            height: "100%",
            backgroundColor: "#E5E5E533",
            overflow: "auto",
          }}
        >
          <div className="p-3 flex-grow-1 overflow-auto">
            <div className="d-flex flex-row justify-content-end mb-2">
              <span className="p-2 fs-6 rounded-2 bg-primary-subtle d-flex flex-column">
                <span>Topic: {model.topic_name || "-"}</span>
                <span>Sub topic: {model.sub_topic_name || "-"}</span>
                <span>QID: {model.question_id || "-"}</span>
              </span>
            </div>
            <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {model.prompt}
            </pre>

            {mandatoryKeywords.length > 0 && (
              <div className="mt-4">
                <h6
                  style={{
                    color: "#333",
                    fontWeight: "bold",
                    marginBottom: "10px",
                  }}
                >
                  Must-Use Keywords and Variables in your code :
                </h6>
                <div
                  className="mb-3 p-3"
                  style={{
                    backgroundColor: "#f8f9fa",
                    border: "1px solid #dee2e6",
                    borderRadius: "8px",
                    fontSize: "13px",
                  }}
                >
                  <span style={{ color: "#212529" }}>
                    {mandatoryKeywords.join(", ")}
                  </span>
                </div>
              </div>
            )}

            {model.examples.length > 0 && (
              <div className="mt-4">
                <h6
                  style={{
                    color: "#333",
                    fontWeight: "bold",
                    marginBottom: "10px",
                  }}
                >
                  Examples:
                </h6>
                {model.examples.map((example, index) => (
                  <div
                    key={index}
                    className="mb-3 p-3"
                    style={{
                      backgroundColor: "#f8f9fa",
                      border: "1px solid #dee2e6",
                      borderRadius: "8px",
                      fontSize: "13px",
                    }}
                  >
                    <div className="mb-2">
                      <strong style={{ color: "#495057" }}>
                        Example {index + 1}:
                      </strong>
                    </div>
                    {example.inputs.length > 0 && (
                      <div className="mb-2">
                        <span style={{ color: "#6c757d", fontWeight: 500 }}>
                          Input:{" "}
                        </span>
                        <span
                          style={{ color: "#212529", whiteSpace: "pre-wrap" }}
                        >
                          {example.inputs.join(", ")}
                        </span>
                      </div>
                    )}
                    {example.output && (
                      <div className="mb-2">
                        <span style={{ color: "#6c757d", fontWeight: 500 }}>
                          Output:{" "}
                        </span>
                        <span
                          style={{ color: "#212529", whiteSpace: "pre-wrap" }}
                        >
                          {example.output}
                        </span>
                      </div>
                    )}
                    {example.explanation && (
                      <div>
                        <span style={{ color: "#6c757d", fontWeight: 500 }}>
                          Explanation:{" "}
                        </span>
                        <span style={{ color: "#212529" }}>
                          {example.explanation}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        style={{
          flex: 1,
          minWidth: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          className="bg-white"
          style={{ height: "45%", backgroundColor: "#E5E5E533" }}
        >
          <AceEditor
            mode="python"
            theme="dreamweaver"
            onChange={setCode}
            value={code}
            fontSize={14}
            showPrintMargin={false}
            wrapEnabled={true}
            className="pe-3"
            style={{
              width: "95%",
              height: "calc(100% - 20px)",
              marginTop: "20px",
              margin: "15px",
            }}
            editorProps={{ $blockScrolling: true }}
            setOptions={{ useWorker: false }}
            placeholder={
              model.template || model.functionCall
                ? ""
                : `Instructions :
1. Don't use input() function. 
2. It is mandatory to use the exact variable names provided in the question or example [variable names are case-sensitive ]


Write your Code here.`
            }
          />
        </div>

        <div
          style={{ height: "6%", backgroundColor: "#E5E5E533" }}
          className="d-flex flex-column justify-content-center processingDiv"
        >
          <div className="d-flex justify-content-between align-items-center h-100">
            <div className="d-flex flex-column justify-content-center">
              {running ? (
                <h5 className="m-0 processingDivHeadingTag">Processing...</h5>
              ) : (
                <>
                  {successMessage && (
                    <h5 className="m-0 ps-1" style={{ fontSize: "14px" }}>
                      {successMessage}
                    </h5>
                  )}
                  {additionalMessage && (
                    <p
                      className="processingDivParaTag m-0 ps-1"
                      style={{ fontSize: "10px" }}
                    >
                      {additionalMessage}
                    </p>
                  )}
                </>
              )}
            </div>
            <div className="d-flex justify-content-end align-items-center">
              <button
                type="button"
                className="btn btn-sm btn-light me-2 processingDivButton"
                style={{
                  whiteSpace: "nowrap",
                  fontSize: "12px",
                  minWidth: "70px",
                  boxShadow: "#888 1px 2px 5px 0px",
                  height: "30px",
                }}
                onClick={handleRunCode}
                disabled={running || !code.trim()}
              >
                RUN CODE
              </button>
            </div>
          </div>
        </div>

        <div
          className="bg-white"
          style={{
            height: "48%",
            backgroundColor: "#E5E5E533",
            position: "relative",
          }}
        >
          <div
            className="p-3"
            style={{
              height: "calc(100% - 10px)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div className="d-flex mb-3" style={{ flexShrink: 0 }}>
              <button
                type="button"
                className={`btn ${
                  activeSection === "output"
                    ? "btn-primary"
                    : "btn-outline-primary"
                } me-2`}
                onClick={() => setActiveSection("output")}
                style={{ fontSize: "12px", padding: "6px 12px" }}
              >
                Output
              </button>
              <button
                type="button"
                className={`btn ${
                  activeSection === "testcases"
                    ? "btn-primary"
                    : "btn-outline-primary"
                }`}
                onClick={() => setActiveSection("testcases")}
                style={{ fontSize: "12px", padding: "6px 12px" }}
              >
                Test Cases
              </button>
            </div>

            {activeSection === "output" && (
              <div style={{ flex: 1, maxHeight: "90%", overflow: "auto" }}>
                {output ? (
                  <pre
                    className="m-0"
                    id="output"
                    style={{
                      fontSize: "12px",
                      fontFamily: "monospace",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      backgroundColor: "#f8f9fa",
                      padding: "10px",
                      borderRadius: "4px",
                      border: "1px solid #e9ecef",
                      margin: "0",
                      overflow: "auto",
                    }}
                  >
                    {output}
                  </pre>
                ) : (
                  !running &&
                  results.length > 0 && (
                    <div className="text-muted small">No stdout. Open Test Cases for results.</div>
                  )
                )}
              </div>
            )}

            {activeSection === "testcases" && (
              results.length > 0 ? (
              <div style={{ flex: 1, maxHeight: "90%", overflow: "auto" }}>
                <div className="d-flex" style={{ height: "calc(100%)" }}>
                  <div
                    className="border-end"
                    style={{
                      width: "20%",
                      overflowY: "auto",
                      padding: "1px 10px 1px 10px",
                      scrollbarWidth: "thin",
                      scrollbarColor: "#c1c1c1 #f1f1f1",
                    }}
                  >
                    {results.map((testCase, index) => (
                      <div
                        key={index}
                        className={`p-2 border-bottom cursor-pointer ${
                          selectedTestCaseIndex === index ? "text-primary" : ""
                        }`}
                        style={{
                          fontSize: "12px",
                          cursor: "pointer",
                          minHeight: "40px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          margin: "6px 0px",
                          borderRadius: "8px",
                          padding: "8px 12px",
                          backgroundColor:
                            selectedTestCaseIndex === index
                              ? "#f2f2f0"
                              : "#f8f9fa",
                          border: "1px solid #dee2e6",
                          color:
                            selectedTestCaseIndex === index
                              ? "#007bff"
                              : "#212529",
                        }}
                        onClick={() => setSelectedTestCaseIndex(index)}
                      >
                        <span>{testCase.id}</span>
                        {testCase.passed ? (
                          <span className="text-success">✓</span>
                        ) : (
                          <span className="text-danger">✗</span>
                        )}
                      </div>
                    ))}
                  </div>
                  <div
                    className="px-5 pt-1 pb-3"
                    style={{ width: "80%", overflowY: "auto" }}
                  >
                    {selectedTestCaseIndex !== null &&
                      results[selectedTestCaseIndex] && (
                        <div>
                          <div className="mb-3">
                            <strong>Status: </strong>
                            <span
                              className={
                                results[selectedTestCaseIndex].passed
                                  ? "text-success"
                                  : "text-danger"
                              }
                            >
                              {results[selectedTestCaseIndex].passed
                                ? "Passed"
                                : "Failed"}
                            </span>
                          </div>
                          {(() => {
                            const row = results[selectedTestCaseIndex];
                            const tc = model.testCases[
                              selectedTestCaseIndex
                            ] as {
                              Testcase?: { Value?: string[]; Output?: string };
                            };
                            const tcValues = tc?.Testcase?.Value;
                            const value =
                              row.input ??
                              (Array.isArray(tcValues)
                                ? tcValues.join("\n")
                                : undefined);
                            const expected =
                              row.expected ?? tc?.Testcase?.Output;
                            const actual = row.actual ?? row.detail;
                            if (row.id === "Result") {
                              return (
                                <div className="mb-3">
                                  <strong>Result: </strong>
                                  <span
                                    className={
                                      row.passed
                                        ? "text-success"
                                        : "text-danger"
                                    }
                                  >
                                    {row.passed ? "Passed" : "Failed"}
                                  </span>
                                </div>
                              );
                            }
                            return (
                              <>
                                {value != null && value !== "" && (
                                  <div className="mb-3">
                                    <strong>Input:</strong>
                                    <div
                                      className="mt-1 p-2 bg-light rounded"
                                      style={{
                                        fontSize: "11px",
                                        fontFamily: "monospace",
                                      }}
                                    >
                                      {String(value)}
                                    </div>
                                  </div>
                                )}
                                {expected != null && expected !== "" && (
                                  <div className="mb-3">
                                    <strong>Expected Output:</strong>
                                    <div
                                      className="mt-1 p-2 bg-light rounded"
                                      style={{
                                        fontSize: "11px",
                                        fontFamily: "monospace",
                                      }}
                                    >
                                      {String(expected)}
                                    </div>
                                  </div>
                                )}
                                {actual != null && actual !== "" && (
                                  <div className="mb-3">
                                    <strong>Actual Output:</strong>
                                    <div
                                      className="mt-1 p-2 bg-light rounded"
                                      style={{
                                        fontSize: "11px",
                                        fontFamily: "monospace",
                                      }}
                                    >
                                      {String(actual)}
                                    </div>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      )}
                    {selectedTestCaseIndex === null && (
                      <div
                        className="text-center text-muted"
                        style={{ marginTop: "50px" }}
                      >
                        Click on a test case to view details
                      </div>
                    )}
                  </div>
                </div>
              </div>
              ) : (
                <div className="text-muted small">
                  Run code to see test case results.
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PythonCodingPreview;
